/** Витрина догоняет склад, не дожидаясь перезапуска.
 *
 * Mini App живёт открытым часами: человек ушёл в переписку, вернулся — а вкус,
 * который к тому времени разобрали, всё ещё показан свободным. Узнаёт он об
 * этом отказом на оформлении, и это худший момент.
 *
 * Спрашиваем не каталог, а отпечаток наличия — несколько байт. Совпал —
 * ничего не делаем; поменялся — перечитываем каталог целиком, со свежими
 * вкусами и потолками корзины.
 *
 * Опрашиваем только видимую витрину: свёрнутый Mini App никому не показывает
 * устаревшее, а батарею и сервер бережём. Зато возвращение на экран — первый
 * повод спросить: именно там устаревание и заметно.
 */
import { useEffect, useRef } from 'react';
import { request } from '@/shared/api';

/** Как часто спрашивать, пока витрина на экране. */
const EVERY_MS = 45_000;

export function useStockPulse(onChanged: () => void) {
  // Колбэк в ref: интервал не должен пересобираться из-за новой ссылки
  const changed = useRef(onChanged);
  changed.current = onChanged;

  useEffect(() => {
    /** Последний известный отпечаток. Пустая строка — ещё не спрашивали. */
    let known = '';
    let alive = true;

    const ask = async () => {
      if (!alive || document.visibilityState !== 'visible') return;
      try {
        const answer = await request<{ stamp: string }>('/api/stock/pulse', {});
        const fresh = String(answer.stamp ?? '');
        if (!alive || !fresh) return;
        // Первый ответ — точка отсчёта: каталог только что прочитан
        if (known && fresh !== known) changed.current();
        known = fresh;
      } catch {
        // Сеть моргнула — молчим: следующий заход через полминуты
      }
    };

    void ask();
    const timer = setInterval(() => void ask(), EVERY_MS);
    const wake = () => {
      if (document.visibilityState === 'visible') void ask();
    };
    document.addEventListener('visibilitychange', wake);

    return () => {
      alive = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', wake);
    };
  }, []);
}
